import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Spin, Table, Tooltip, Modal, message } from "antd";
import { useProjectContext } from "../../context/ProjectContext";
import { getConfig } from "../../config";
import "./MainPage.css";
import hafnerLogo from "../../images/hafner_logo.png";
import muessigLogo from "../../images/muessig_logo.jpeg";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { getToken } from "../../security/tokenStorage";
import { ExportOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { signalSplashReady } from "../../utils/splashReady";

dayjs.extend(isSameOrAfter);

type WarningItem = {
    id: number;
    description: string;
    protocolDescription: string;
    target: string;
    projectNo: string;
    timestamp: string | null;
    persistanceUnit?: string;
};

const MainPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const [userReady, setUserReady] = useState(false); // User wurde final geladen/entschieden
    const lastManagerNoRef = React.useRef<string | null>(null);
    const { API_BASE_URL } = getConfig();
    const today = dayjs();
    const [projectSummaries, setProjectSummaries] = useState<ProjectSummary[]>([]);
    const {
        setSelectedManager,
        setSelectedManagerNo,
        setSelectedProject,
        selectedManagerNo
    } = useProjectContext();
    const effectiveManagerNo =
        userReady
            ? (selectedManagerNo && selectedManagerNo !== "Alle"
                ? selectedManagerNo
                : (user?.technicianId ? String(user.technicianId) : null))
            : null;

    // --- NEU: Warnungen-Status ---
    const [warnings, setWarnings] = useState<WarningItem[]>([]);
    const [warningsLoading, setWarningsLoading] = useState<boolean>(false);

    const openTaskManagementNewTab = (projectNo: string | number) => {
        const pn = String(projectNo);
        localStorage.setItem("selectedProject", pn); // <-- wie bei config
        const url = new URL(`${window.location.origin}/task-management`);
        url.searchParams.set("project", pn);
        window.open(url.toString(), "_blank", "noopener");
    };

    const navigate = useNavigate();

    const openConfigurationForProjectNewTab = (projectNo: string | number) => {
        const pn = String(projectNo);
        localStorage.setItem("selectedProject", pn);
        const url = new URL(`${window.location.origin}/configuration`);
        url.searchParams.set("tab", "4"); // Untertab "Auftragskarten"
        url.searchParams.set("project", pn); // Projekt als Query-Param
        window.open(url.toString(), "_blank", "noopener");
    };

    // --- Hilfsfunktion zum Laden der Warnungen ---
    const loadWarnings = (managerNo: string, controller?: AbortController) => {
        setWarningsLoading(true);

        axios
            .get<WarningItem[]>(
                `${API_BASE_URL}/getNotValidDataConfigurations`,
                {
                    params: {
                        auth: "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU",
                        technicianId: managerNo
                    },
                    signal: controller?.signal
                }
            )
            .then((res) => {
                const rows = Array.isArray(res.data) ? res.data : [];
                const format = "DD.MM.YYYY HH:mm";
                const sorted = [...rows].sort((a, b) => {
                    const ta = a.timestamp ? dayjs(a.timestamp, format).valueOf() : -Infinity;
                    const tb = b.timestamp ? dayjs(b.timestamp, format).valueOf() : -Infinity;
                    return tb - ta;
                });
                setWarnings(sorted);
            })
            .catch((err) => {
                if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
                    console.error("Fehler beim Laden der Warnungen:", err);
                    setWarnings([]);
                }
            })
            .finally(() => setWarningsLoading(false));
    };

    useEffect(() => {
        const token = getToken();
        if (!token) {
            setLoading(false);
            setUser(null);
            setUserReady(true);
            return;
        }
        axios
            .get<User>(`${API_BASE_URL}/getUserByToken`, {
                params: { auth: "khM0UwF7y0ItxRuazsE1RRRdX0WwLKPDjgF2PEfL", token }
            })
            .then((response) => {
                const user = response.data;
                setUser(user);
                if (user) {
                    setSelectedManager(user.abacusName);
                    setSelectedManagerNo(user.technicianId ? String(user.technicianId) : null);
                    setSelectedProject("Alle");
                    localStorage.setItem("selectedProjectManager", user.abacusName);
                    localStorage.setItem("selectedProjectManagerNo", String(user.technicianId));
                }
            })
            .catch(() => {
                setUser(null);
            })
            .finally(() => {
                setLoading(false);
                setUserReady(true);
            });
    }, [API_BASE_URL, setSelectedManager, setSelectedManagerNo, setSelectedProject]);

    useEffect(() => {
        if (!userReady) return;

        const managerNo = effectiveManagerNo;
        if (!managerNo) {
            setProjectSummaries([]);
            lastManagerNoRef.current = null;
            // Auch das ist ein Endzustand - ohne Meldung wartete das Startbild bis zur Obergrenze.
            signalSplashReady("uebersicht");
            return;
        }

        // Kein erneuter Call, wenn sich die effektive Nummer nicht geändert hat
        if (lastManagerNoRef.current === managerNo) return;
        lastManagerNoRef.current = managerNo;

        const controller = new AbortController();
        axios
            .get<ProjectSummary[]>(
                `${API_BASE_URL}/getProjectSummaryDashboard`,
                { params: { technicianId: managerNo }, signal: controller.signal }
            )
            .then((res) => {
                setProjectSummaries(res.data);
                signalSplashReady("uebersicht");
            })
            .catch((err) => {
                if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
                    console.error("Fehler beim Laden der Projektübersicht:", err);
                    setProjectSummaries([]);
                    // Die Seite steht trotzdem (leerer Zustand), also melden statt warten.
                    signalSplashReady("uebersicht");
                }
            });

        return () => controller.abort();
    }, [API_BASE_URL, userReady, effectiveManagerNo]);

    // 3) Warnungen laden – NUR wenn user.technicianId vorhanden
    useEffect(() => {
        if (!userReady) return;
        const managerNo = effectiveManagerNo;
        if (!managerNo) {
            setWarnings([]);
            return;
        }

        const controller = new AbortController();
        loadWarnings(managerNo, controller);

        return () => controller.abort();
    }, [API_BASE_URL, userReady, effectiveManagerNo]);

    const getBufferColor = (buffer: number | null): string => {
        if (buffer === null) return "transparent";
        if (buffer <= 20) return `rgb(255, ${Math.round(255 * (buffer / 20))}, 0)`;
        if (buffer <= 60)
            return `rgb(${Math.round(255 * (1 - (buffer - 20) / 40))}, ${Math.round(
                180 + 75 * (1 - (buffer - 20) / 40)
            )}, 0)`;
        return "rgb(0, 180, 0)";
    };

    const events = [
        {
            title: "MLS",
            subtitle: "KW 46 (Do. 13.11. - Fr. 14.11.)",
            logo: muessigLogo,
            start: dayjs("2025-11-13"),
            end: dayjs("2025-11-14")
        },
        {
            title: "Mitarbeiter-Informationsanlass",
            subtitle: "KW 47 (Mi. 19.11.)",
            logo: muessigLogo,
            start: dayjs("2025-11-19"),
            end: dayjs("2025-11-19")
        },
        {
            title: "Weihnachtsfeier",
            subtitle: "KW 50 (Mi. 12.12.)",
            logo: muessigLogo,
            start: dayjs("2025-12-12"),
            end: dayjs("2025-12-12")
        }
    ];

    const columns = [
        {
            title: "Projekt-Nr.",
            dataIndex: "projectNo",
            key: "projectNo",
            sorter: (a: ProjectSummary, b: ProjectSummary) =>
                String(a.projectNo).localeCompare(String(b.projectNo), "de", {
                    numeric: true,
                    sensitivity: "base"
                }),
            width: 100
        },
        {
            title: "Projekt",
            dataIndex: "project",
            key: "project",
            sorter: (a: ProjectSummary, b: ProjectSummary) =>
                (a.project || "").localeCompare(b.project || ""),
            ellipsis: true,
            width: 380
        },
        {
            title: "Kunde",
            dataIndex: "customer",
            key: "customer",
            sorter: (a: ProjectSummary, b: ProjectSummary) =>
                (a.customer || "").localeCompare(b.customer || ""),
            ellipsis: true,
            width: 250
        },
        {
            title: "Vorgang",
            dataIndex: "task",
            width: 200,
            key: "task",
            render: (html: string) => (
                <div style={{ maxHeight: 220, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: html }} />
            )
        },
        {
            title: "Karte / Produkt",
            dataIndex: "description",
            key: "description",
            width: 320,
            render: (html: string) => (
                <div style={{ maxHeight: 220, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: html }} />
            )
        },
        {
            title: "Verantwortlich",
            dataIndex: "department",
            key: "department",
            width: 230,
            render: (html: string) => (
                <div style={{ maxHeight: 220, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: html }} />
            )
        },
        {
            title: "Puffer (Tage)",
            dataIndex: "minBuffer",
            key: "minBuffer",
            defaultSortOrder: "ascend" as const,
            sorter: (a: ProjectSummary, b: ProjectSummary) => (a.minBuffer ?? 0) - (b.minBuffer ?? 0),
            width: 130,
            render: (buffer: number | null) => (
                <span
                    style={{
                        backgroundColor: getBufferColor(buffer),
                        color: "black",
                        padding: "2px 8px",
                        borderRadius: 8,
                        display: "inline-block",
                        minWidth: 36,
                        textAlign: "center",
                        fontWeight: 600
                    }}
                >
                    {buffer}
                </span>
            )
        },
        {
            title: "",
            key: "openInNewTab",
            width: 48,
            fixed: "right" as const,
            align: "right" as const,
            className: "right-action-cell",
            render: (_: any, record: ProjectSummary) => (
                <Tooltip title="Daten in neuem Tab öffnen">
                    <ExportOutlined
                        onClick={(e) => {
                            e.stopPropagation();
                            openTaskManagementNewTab(record.projectNo);
                        }}
                        style={{ cursor: "pointer", fontSize: 18, color: "#153e8c" }}
                    />
                </Tooltip>
            )
        }
    ];

    // --- Spalten für Warnungen ---
    const warningColumns = [
        {
            title: "Projekt-Nr.",
            dataIndex: "projectNo",
            key: "projectNo",
            width: 120,
            sorter: (a: WarningItem, b: WarningItem) =>
                (a.projectNo || "").localeCompare(b.projectNo || "", "de", {
                    numeric: true,
                    sensitivity: "base"
                })
        },
        {
            title: "Auftragskarte/Produkt",
            dataIndex: "target",
            key: "target",
            width: 160,
            sorter: (a: WarningItem, b: WarningItem) => (a.target || "").localeCompare(b.target || "")
        },
        {
            title: "Bezeichnung",
            dataIndex: "protocolDescription",
            key: "protocolDescription",
            width: 320,
            render: (html: string) => (
                <div style={{ maxHeight: 220, overflow: "auto" }} dangerouslySetInnerHTML={{ __html: html }} />
            )
        },
        {
            title: "Beschreibung",
            dataIndex: "description",
            key: "description",
            width: 300,
            ellipsis: true
        },
        {
            title: "Zeitstempel",
            dataIndex: "timestamp",
            key: "timestamp",
            width: 180,
            sorter: (a: WarningItem, b: WarningItem) => {
                const format = "DD.MM.YYYY HH:mm";
                const ta = a.timestamp ? dayjs(a.timestamp, format).valueOf() : -Infinity;
                const tb = b.timestamp ? dayjs(b.timestamp, format).valueOf() : -Infinity;
                return ta - tb;
            },
            render: (ts: string | null) =>
                ts ? dayjs(ts, "DD.MM.YYYY HH:mm").format("DD.MM.YYYY HH:mm") + " Uhr" : "-"
        },
        {
            title: "",
            key: "openConfig",
            width: 48,
            fixed: "right" as const,
            align: "right" as const,
            className: "right-action-cell",
            render: (_: any, record: WarningItem) => (
                <Tooltip title="Zugehörige Konfiguration in neuem Tab öffnen">
                    <ExportOutlined
                        onClick={(e) => {
                            e.stopPropagation();
                            openConfigurationForProjectNewTab(record.projectNo);
                        }}
                        style={{ cursor: "pointer", fontSize: 18, color: "#153e8c" }}
                    />
                </Tooltip>
            )
        },
    ];

    const visibleEvents = events.filter((event) => !today.isAfter(event.end, "day"));

    if (loading) {
        return (
            <div className="main-page-loading">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="main-page-container">
            <Card className="main-page-card" bordered={false}>
                <div style={{ padding: "20px" }}>
                    {user ? (
                        <>
                            <h2>
                                Willkommen, {user.firstName} {user.secondName}!
                            </h2>
                        </>
                    ) : (
                        <p>Benutzerdaten konnten nicht geladen werden.</p>
                    )}

                    <hr className="main-page-divider" />
                    <h3 className="info-tile-heading">Anstehende Ereignisse</h3>

                    <div className="info-tile-grid">
                        {visibleEvents.map((event, index) => {
                            const isRunning =
                                !today.isBefore(event.start, "day") &&
                                !today.isAfter(event.end, "day");

                            const isFuture = today.isBefore(event.start, "day");
                            return (
                                <div key={index} className="info-tile warning-tile">
                                    <div className="info-tile-header">
                                        <img src={event.logo} alt="Event Logo" className="info-tile-logo" />
                                    </div>
                                    <div className="info-tile-content">
                                        <div className="info-tile-title">{event.title}</div>
                                        <div className="info-tile-subtitle">{event.subtitle}</div>
                                        <div className="info-tile-countdown">
                                            {isFuture && (
                                                <>
                                                    In{" "}
                                                    {Math.max(
                                                        event.start
                                                            .startOf("day")
                                                            .diff(today.startOf("day"), "day"),
                                                        0
                                                    )}{" "}
                                                    Tagen
                                                </>
                                            )}
                                            {isRunning && (
                                                <span className="event-running">
                                                    <span className="blinking-dot" /> Läuft
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* --- NEUER BEREICH: Warnungen --- */}
                    <hr className="main-page-divider" />
                    <h3 className="info-tile-heading">Wichtige Hinweise</h3>

                    <div style={{ width: "100%", overflowX: "auto", marginBottom: "30px" }}>
                        {warningsLoading ? (
                            <div style={{ padding: "16px 0" }}>
                                <Spin />
                            </div>
                        ) : warnings.length === 0 ? (
                            <div style={{ fontStyle: "italic", color: "#999", marginBottom: "10px" }}>
                                Keine Hinweise vorhanden.
                            </div>
                        ) : (
                            <Table<WarningItem>
                                rowKey={(r) => String(r.id)}
                                columns={warningColumns}
                                dataSource={[...warnings]}
                                pagination={{ pageSize: 50 }}
                                size="middle"
                                sticky
                                style={{ width: "100%", minWidth: "900px" }}
                            />
                        )}
                    </div>

                    <hr className="main-page-divider" />
                    <h3 className="info-tile-heading">Projekt-Übersicht</h3>

                    {projectSummaries.length === 0 ? (
                        <div style={{ fontStyle: "italic", color: "#999", marginBottom: "30px" }}>
                            Keine Projekte für die aktuelle Auswahl.
                        </div>
                    ) : (
                        <div style={{ width: "100%", overflowX: "auto", marginBottom: "30px" }}>
                            <Table<ProjectSummary>
                                rowKey={(r) => r.projectNo}
                                columns={columns}
                                dataSource={[...projectSummaries]}
                                pagination={{ pageSize: 50 }}
                                size="middle"
                                sticky
                                style={{
                                    width: "100%",
                                    minWidth: "1200px"
                                }}
                            />
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default MainPage;